import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

interface TocItem {
  id: string;
  label: string;
}

const tocItems: TocItem[] = [
  { id: 'aceptacion', label: '1. Aceptación de los Términos' },
  { id: 'alcance', label: '2. Alcance de los Servicios' },
  { id: 'definiciones', label: '3. Definiciones' },
  { id: 'cuenta', label: '4. Registro y Cuenta' },
  { id: 'obligaciones', label: '5. Obligaciones del Usuario' },
  { id: 'contenido', label: '6. Contenido del Usuario' },
  { id: 'prohibido', label: '7. Contenido y Conducta Prohibida' },
  { id: 'propiedad', label: '8. Propiedad Intelectual' },
  { id: 'suspension', label: '9. Suspensión y Terminación' },
  { id: 'limitacion', label: '10. Limitación de Responsabilidad' },
  { id: 'modificaciones', label: '11. Modificaciones' },
  { id: 'contacto', label: '12. Contacto' },
];

function SectionBlock({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-bold text-foreground-950 mb-4">{title}</h2>
      <div className="text-foreground-700 text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i} className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-[0.6em] before:w-1.5 before:h-1.5 before:rounded-full before:bg-primary-500/60 text-foreground-600">
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState('aceptacion');

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20minimalist%20legal%20documents%20background%20with%20soft%20flowing%20lines%2C%20warm%20cream%20and%20charcoal%20gradient%2C%20subtle%20paper%20texture%2C%20elegant%20typographic%20patterns%2C%20professional%20and%20clean%20aesthetic%2C%20light%20shadows%2C%20legal%20theme%20with%20modern%20minimal%20design%2C%20no%20text%20no%20logos%2C%20abstract%20geometric%20shapes%20fading%20into%20background&width=1600&height=600&seq=terms-hero-bg&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/95 to-background-50"></div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-5">
            <i className="ri-file-text-line"></i>
            Documento Legal
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Términos y Condiciones de Uso</h1>
          <p className="text-foreground-500 text-sm max-w-2xl mx-auto">
            Última actualización: 8 de junio de 2026. Estos términos regulan el uso de RadioFrecuenciaWeb como emisora, oyente o creador de contenido.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 pb-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Sidebar TOC */}
            <aside className="lg:w-60 shrink-0">
              <div className="sticky top-24 bg-background-100/80 rounded-2xl border border-background-200/70 p-4">
                <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-widest mb-3">Contenido</p>
                <nav className="space-y-0.5">
                  {tocItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSection(item.id);
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className={`block px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                        activeSection === item.id
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60'
                      }`}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0 space-y-10">
              <SectionBlock id="aceptacion" title="1. Aceptación de los Términos">
                <p>
                  Al acceder o utilizar RadioFrecuenciaWeb (en adelante, "la Plataforma"), usted acepta de manera expresa
                  e irrevocable los presentes Términos y Condiciones. Si no está de acuerdo con alguno de los términos aquí
                  establecidos, debe abstenerse de utilizar la Plataforma.
                </p>
                <p>
                  El uso de la Plataforma implica que usted ha leído, comprendido y aceptado estos Términos en su
                  totalidad. Nos reservamos el derecho de modificar estos términos en cualquier momento, y dichas
                  modificaciones entrarán en vigor inmediatamente después de su publicación.
                </p>
              </SectionBlock>

              <SectionBlock id="alcance" title="2. Alcance de los Servicios">
                <p>
                  RadioFrecuenciaWeb es una plataforma de administración, gestión, promoción e interacción para emisoras
                  de radio por internet. La Plataforma actúa como un centro de control integral que permite a las emisoras
                  gestionar su presencia digital, interactuar con su audiencia, administrar su programación, y acceder a
                  herramientas de análisis y moderación.
                </p>
                
                <h3 className="text-sm font-bold text-foreground-800 mt-5 mb-2">RadioFrecuenciaWeb SÍ ofrece:</h3>
                <BulletList items={[
                  'Administración de radio online con herramientas de gestión.',
                  'Perfil público de emisora con identidad visual y marca.',
                  'Frecuencia RFW exclusiva dentro del directorio de la Plataforma.',
                  'Cabina interactiva para locutores con chat en vivo, mensajes y moderación.',
                  'Chat en tiempo real entre oyentes y la emisora durante las transmisiones.',
                  'Comunidad de oyentes con seguidores, interacciones y notificaciones.',
                  'Organización y publicación de eventos propios de la emisora.',
                  'Galería de fotos y videos con gestión de contenido multimedia.',
                  'Transmisiones en vivo (Lives) dentro de la Plataforma si el plan lo permite.',
                  'Retransmisión futura a redes sociales (funcionalidad en desarrollo).',
                  'Directorio oficial de emisoras con búsqueda por categoría, género y ubicación.',
                  'Aplicación móvil futura para oyentes (en desarrollo).',
                ]} />

                <h3 className="text-sm font-bold text-foreground-800 mt-5 mb-2">RadioFrecuenciaWeb NO ofrece:</h3>
                <BulletList items={[
                  '<strong>Servidor de streaming de audio.</strong> La Plataforma no provee infraestructura de transmisión de audio.',
                  '<strong>Servidores SHOUTcast o Icecast.</strong> No comercializamos ni administramos servidores de streaming de terceros.',
                  '<strong>AutoDJ.</strong> No ofrecemos software de automatización radial ni gestión de playlists automáticas.',
                  '<strong>Ancho de banda de señal de radio.</strong> No proveemos conectividad ni tráfico de datos para la transmisión de audio.',
                  '<strong>Licencias musicales.</strong> No gestionamos ni proveemos licencias de derechos de autor, regalías, ni permisos de reproducción musical.',
                  '<strong>Servicios de radiodifusión tradicional.</strong> No operamos como estación de radio ni como organismo de radiodifusión.',
                ]} />

                <p className="mt-4">
                  <strong>Cada emisora es responsable de contratar y mantener su propio proveedor de streaming.</strong>{' '}
                  La radio proporciona su Stream URL para que RadioFrecuenciaWeb pueda reproducir su señal dentro de
                  la Plataforma. Si la URL falla, se cae, no transmite o el proveedor externo tiene problemas,
                  RadioFrecuenciaWeb no es responsable por esa interrupción. La disponibilidad, estabilidad, calidad
                  de audio, continuidad de transmisión, consumo de ancho de banda y funcionamiento del servicio de
                  streaming son responsabilidad exclusiva del proveedor de transmisión contratado por la emisora y/o
                  de la propia emisora.
                </p>

                <p className="mt-4">
                  <strong>Derechos musicales y de autor.</strong> Cada radio es la única responsable de obtener,
                  mantener y cumplir con todos los permisos, licencias, regalías y autorizaciones de derechos de
                  autor correspondientes a la música, programas, podcasts, eventos deportivos, retransmisiones y
                  cualquier otro contenido protegido que difunda a través de la Plataforma. Esto incluye, sin
                  limitación, las obligaciones ante entidades de gestión colectiva como SADAIC, ASCAP, BMI, SESAC,
                  AGADU, SAYCO, APDAYC y cualquier otra entidad aplicable en su jurisdicción. RadioFrecuenciaWeb
                  actúa únicamente como plataforma tecnológica de administración y no asume responsabilidad sobre
                  los contenidos transmitidos por las emisoras ni sobre los derechos de autor asociados a dichos
                  contenidos. La Plataforma podrá suspender o cancelar cuentas que incumplan la legislación aplicable,
                  reciban reclamaciones válidas por infracción de derechos de autor, abusen de los servicios o
                  utilicen la Plataforma para actividades no autorizadas.
                </p>
              </SectionBlock>

              <SectionBlock id="definiciones" title="3. Definiciones">
                <p>Para los efectos de estos Términos, se entenderá por:</p>
                <BulletList items={[
                  '<strong>Plataforma:</strong> RadioFrecuenciaWeb, incluyendo su sitio web, aplicaciones móviles, y todos los servicios asociados.',
                  '<strong>Radio:</strong> Persona física o jurídica que registra una emisora en la Plataforma para transmitir contenido de audio.',
                  '<strong>Oyente:</strong> Persona que accede a la Plataforma para escuchar radios, sin necesidad de registro.',
                  '<strong>Usuario:</strong> Cualquier persona que utiliza la Plataforma, ya sea como Radio, Oyente o cualquier otro rol.',
                  '<strong>Stream:</strong> Flujo de audio en tiempo real transmitido por una Radio a través de la Plataforma.',
                  '<strong>Frecuencia RFW:</strong> Identificador único asignado a cada Radio dentro de la Plataforma, en formato numérico (ej. 96.30 RFW).',
                  '<strong>Cabina:</strong> Sistema de mensajería en tiempo real entre Oyentes y la Radio durante una transmisión.',
                ]} />
              </SectionBlock>

              <SectionBlock id="cuenta" title="4. Registro y Cuenta">
                <p>
                  Para registrar una Radio en la Plataforma, es necesario completar el formulario de registro con
                  información veraz y actualizada. El registro como Oyente no es obligatorio para escuchar radios,
                  pero puede ser requerido para acceder a ciertas funcionalidades.
                </p>
                <p>
                  Usted es responsable de mantener la confidencialidad de sus credenciales de acceso y de todas las
                  actividades que ocurran bajo su cuenta. Debe notificarnos inmediatamente sobre cualquier uso no
                  autorizado de su cuenta.
                </p>
                <p>
                  RadioFrecuenciaWeb se reserva el derecho de rechazar cualquier solicitud de registro, suspender
                  o cancelar cuentas que violen estos Términos, sin previo aviso y a su entera discreción.
                </p>
              </SectionBlock>

              <SectionBlock id="obligaciones" title="5. Obligaciones del Usuario">
                <p>Todos los usuarios de la Plataforma se comprometen a:</p>
                <BulletList items={[
                  'Utilizar la Plataforma de conformidad con la legislación aplicable y estos Términos.',
                  'No interferir con el funcionamiento normal de la Plataforma ni intentar acceder a áreas restringidas.',
                  'No utilizar la Plataforma para fines ilegales, fraudulentos o no autorizados.',
                  'Respetar los derechos de propiedad intelectual de RadioFrecuenciaWeb y de terceros.',
                  'No realizar ingeniería inversa, descompilar o intentar extraer el código fuente de la Plataforma.',
                ]} />
              </SectionBlock>

              <SectionBlock id="contenido" title="6. Contenido del Usuario">
                <p>
                  Las Radios son las únicas responsables por el contenido que transmiten a través de la Plataforma,
                  incluyendo audio, imágenes, videos, texto y cualquier otro material. Al subir o transmitir contenido,
                  usted garantiza que posee todos los derechos necesarios para hacerlo.
                </p>
                <p>
                  RadioFrecuenciaWeb no reclama propiedad sobre el contenido generado por los usuarios. Sin embargo,
                  al transmitir contenido a través de la Plataforma, usted nos otorga una licencia mundial, no exclusiva
                  y libre de regalías para almacenar, reproducir y distribuir dicho contenido exclusivamente dentro
                  de la Plataforma.
                </p>
              </SectionBlock>

              <SectionBlock id="prohibido" title="7. Contenido y Conducta Prohibida">
                <p>Queda estrictamente prohibido en la Plataforma:</p>
                <BulletList items={[
                  '<strong>Contenido sexual explícito:</strong> Material pornográfico, obsceno o sexualmente explícito en audio, imágenes o video.',
                  '<strong>Discurso de odio:</strong> Contenido que promueva la discriminación, el odio o la violencia contra personas o grupos por motivos de raza, etnia, religión, género, orientación sexual, discapacidad o cualquier otra condición.',
                  '<strong>Acoso e intimidación:</strong> Conductas dirigidas a hostigar, amenazar o intimidar a otros usuarios.',
                  '<strong>Contenido violento:</strong> Material que glorifique, promueva o incite a la violencia.',
                  '<strong>Spam y publicidad engañosa:</strong> Publicación masiva de contenido no solicitado, esquemas piramidales o publicidad fraudulenta.',
                  '<strong>Violación de derechos de autor:</strong> Transmisión de música, programas o cualquier contenido protegido sin la autorización correspondiente.',
                  '<strong>Suplantación de identidad:</strong> Hacerse pasar por otra persona, entidad o Radio sin autorización.',
                  '<strong>Actividades ilegales:</strong> Cualquier conducta que viole leyes locales, nacionales o internacionales.',
                ]} />
              </SectionBlock>

              <SectionBlock id="propiedad" title="8. Propiedad Intelectual">
                <p>
                  La Plataforma, incluyendo su diseño, código fuente, bases de datos, logotipos, marcas y demás
                  elementos, es propiedad exclusiva de RadioFrecuenciaWeb y está protegida por las leyes de
                  propiedad intelectual aplicables.
                </p>
                <p>
                  Las Radios conservan todos los derechos sobre sus marcas, logotipos y contenido original transmitido
                  a través de la Plataforma. RadioFrecuenciaWeb no adquiere ningún derecho de propiedad sobre
                  dicho contenido más allá de la licencia necesaria para operar la Plataforma.
                </p>
              </SectionBlock>

              <SectionBlock id="suspension" title="9. Suspensión y Terminación">
                <p>
                  RadioFrecuenciaWeb se reserva el derecho de suspender o terminar el acceso a la Plataforma,
                  de forma temporal o permanente, en los siguientes casos:
                </p>
                <BulletList items={[
                  'Violación de cualquiera de estos Términos y Condiciones.',
                  'Transmisión de contenido prohibido según lo establecido en la Sección 7.',
                  'Inactividad prolongada de la Radio sin transmisión durante un período continuo de 90 días.',
                  'Caída del stream de audio por un período continuo superior a 72 horas sin notificación.',
                  'Uso fraudulento de la Plataforma o actividades que comprometan su seguridad.',
                  'Por solicitud de autoridades competentes en cumplimiento de obligaciones legales.',
                ]} />
                <p>
                  En caso de suspensión, la Radio será notificada a través del correo electrónico registrado,
                  indicando el motivo y, cuando corresponda, las acciones necesarias para restablecer el servicio.
                </p>
              </SectionBlock>

              <SectionBlock id="limitacion" title="10. Limitación de Responsabilidad">
                <p>
                  RadioFrecuenciaWeb proporciona la Plataforma "tal cual" y "según disponibilidad", sin garantías
                  de ningún tipo, expresas o implícitas. No garantizamos que el servicio sea ininterrumpido,
                  libre de errores o completamente seguro.
                </p>
                <p>
                  En ningún caso RadioFrecuenciaWeb será responsable por daños directos, indirectos, incidentales,
                  especiales o consecuentes que resulten del uso o la imposibilidad de uso de la Plataforma,
                  incluyendo pero no limitado a pérdida de datos, interrupción del negocio o daños a la reputación.
                </p>
                <p>
                  Las Radios son las únicas responsables por las licencias, derechos de autor y permisos necesarios
                  para el contenido que transmiten. RadioFrecuenciaWeb no asume ninguna responsabilidad por
                  infracciones de propiedad intelectual cometidas por los usuarios.
                </p>
              </SectionBlock>

              <SectionBlock id="modificaciones" title="11. Modificaciones">
                <p>
                  Nos reservamos el derecho de modificar estos Términos en cualquier momento. Las modificaciones
                  entrarán en vigor en el momento de su publicación en la Plataforma. Es responsabilidad del
                  usuario revisar periódicamente estos Términos.
                </p>
                <p>
                  El uso continuado de la Plataforma después de la publicación de modificaciones constituye
                  la aceptación de los nuevos términos. Si no está de acuerdo con las modificaciones, deberá
                  dejar de utilizar la Plataforma.
                </p>
              </SectionBlock>

              <SectionBlock id="contacto" title="12. Contacto">
                <p>Para cualquier consulta relacionada con estos Términos y Condiciones, puede contactarnos a través de:</p>
                <BulletList items={[
                  'Correo electrónico: legal@radiofrecuenciaweb.com',
                  'Formulario de contacto disponible en la Plataforma.',
                  'Dirección postal: Santa Rosa, California, Estados Unidos.',
                ]} />
              </SectionBlock>

              {/* Bottom CTA */}
              <div className="mt-10 p-6 bg-background-100/80 rounded-2xl border border-background-200/70 text-center">
                <p className="text-foreground-700 text-sm mb-3">¿Aún tenés dudas sobre nuestros términos?</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link to="/privacy" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-shield-check-line mr-1.5"></i>
                    Política de Privacidad
                  </Link>
                  <Link to="/cookies" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-file-settings-line mr-1.5"></i>
                    Política de Cookies
                  </Link>
                  <Link to="/radio-rules" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-radio-line mr-1.5"></i>
                    Reglas para Radios
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}